import React, { useEffect, useState } from 'react';
import { Elements, ExpressCheckoutElement, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Blocco di pagamento per UN singolo PaymentIntent (uno per artigiano).
 * Renderizza:
 *  - Header con nome artigiano + subtotale
 *  - ExpressCheckoutElement (Google Pay / Link / PayPal / Apple Pay quando dominio verificato)
 *  - Divisore "oppure paga con carta"
 *  - PaymentElement (form inline carta)
 *  - Bottone "Paga X€"
 *
 * Props:
 *   intent: { crafter_id, crafter_name, client_secret, amount, currency, items, payment_intent_id }
 *   stripePromise: Promise<Stripe> caricato una volta a livello parent
 *   returnUrl: URL a cui Stripe redirige dopo confirmPayment
 *   onSuccess: callback quando il pagamento (client-side) ha successo (per UI, il finalize e' via webhook)
 */

const InnerForm = ({ intent, returnUrl, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const runConfirm = async () => {
    const finalReturnUrl = `${returnUrl}?pi=${intent.payment_intent_id}&crafter_id=${intent.crafter_id}`;
    // Stripe richiede elements.submit() prima di confirmPayment quando
    // usiamo il PaymentElement con redirect condizionale.
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMsg(submitError.message || 'Dati di pagamento non validi');
      toast.error(submitError.message || 'Dati di pagamento non validi');
      setProcessing(false);
      return;
    }
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: intent.client_secret,
      confirmParams: { return_url: finalReturnUrl },
      redirect: 'if_required',
    });
    if (error) {
      setErrorMsg(error.message || 'Pagamento fallito');
      toast.error(error.message || 'Pagamento fallito');
      setProcessing(false);
      return;
    }
    // Pagamento riuscito senza redirect (carte non-3DS, wallets istantanei)
    if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
      try {
        // ATTENDI la creazione ordini + email + stock lato client PRIMA di
        // navigare via, altrimenti il carrello sparisce e i side-effect saltano.
        if (typeof onSuccess === 'function') {
          await onSuccess(intent, paymentIntent);
        }
      } catch (sideErr) {
        console.error('post-payment side effects error:', sideErr);
        // Continua comunque con il redirect: il pagamento è già andato.
      }
      window.location.href =
        `${finalReturnUrl}&payment_intent=${paymentIntent.id}` +
        `&payment_intent_client_secret=${paymentIntent.client_secret || intent.client_secret}` +
        `&redirect_status=${paymentIntent.status}`;
      return;
    }
    // Altri stati (requires_action, ecc.): Stripe si sarà occupato del redirect
    // già oppure c'è un problema di flow; ripristino il pulsante.
    setProcessing(false);
  };

  const handleConfirm = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setErrorMsg(null);
    try {
      await runConfirm();
    } catch (err) {
      console.error('confirmPayment error:', err);
      setErrorMsg(err?.message || 'Errore imprevisto durante il pagamento');
      toast.error(err?.message || 'Errore imprevisto durante il pagamento');
      setProcessing(false);
    }
  };

  const handleExpressConfirm = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    setErrorMsg(null);
    try {
      await runConfirm();
    } catch (err) {
      console.error('express confirmPayment error:', err);
      setErrorMsg(err?.message || 'Errore imprevisto durante il pagamento');
      toast.error(err?.message || 'Errore imprevisto durante il pagamento');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleConfirm} className="space-y-4">
      {/* Express buttons (Apple/Google Pay, Link, PayPal) */}
      <div data-testid={`express-buttons-${intent.crafter_id}`}>
        <ExpressCheckoutElement
          onConfirm={handleExpressConfirm}
          options={{
            layout: { maxColumns: 2, maxRows: 2 },
            buttonHeight: 48,
            buttonTheme: {
              applePay: 'black',
              googlePay: 'black',
              paypal: 'gold',
            },
            paymentMethods: {
              applePay: 'auto',
              googlePay: 'auto',
              link: 'auto',
              paypal: 'auto',
              amazonPay: 'never',
            },
          }}
        />
      </div>

      {/* Separatore */}
      <div className="relative flex items-center py-2">
        <div className="flex-grow border-t border-slate-200" />
        <span className="mx-3 text-xs uppercase tracking-widest text-slate-400">
          oppure paga con carta
        </span>
        <div className="flex-grow border-t border-slate-200" />
      </div>

      {/* Payment Element (form carta inline) */}
      <PaymentElement
        options={{
          layout: 'tabs',
          defaultValues: { billingDetails: { email: intent.buyer_email } },
        }}
      />

      {errorMsg && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {errorMsg}
        </div>
      )}

      <Button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-[#008080] hover:bg-[#008080]/90 text-white py-3 text-base font-semibold"
        data-testid={`pay-btn-${intent.crafter_id}`}
      >
        {processing ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Elaborazione...
          </>
        ) : (
          <>Paga {intent.amount.toFixed(2)}€</>
        )}
      </Button>
    </form>
  );
};

const StripeExpressCheckoutBlock = ({ intent, stripePromise, returnUrl, onSuccess }) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (intent?.client_secret) setReady(true);
  }, [intent]);

  if (!ready) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Preparazione pagamento...
      </div>
    );
  }

  const options = {
    clientSecret: intent.client_secret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#008080',
        colorText: '#0f172a',
        colorBackground: '#ffffff',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        borderRadius: '10px',
      },
    },
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4"
      data-testid={`checkout-block-${intent.crafter_id}`}
    >
      <div className="flex items-center justify-between pb-3 border-b border-slate-100">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-semibold">
            Paga a
          </p>
          <p className="text-base font-semibold text-slate-800">{intent.crafter_name}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Totale</p>
          <p className="text-lg font-bold text-slate-800">{intent.amount.toFixed(2)}€</p>
        </div>
      </div>

      <Elements stripe={stripePromise} options={options}>
        <InnerForm intent={intent} returnUrl={returnUrl} onSuccess={onSuccess} />
      </Elements>
    </div>
  );
};

export default StripeExpressCheckoutBlock;
export { loadStripe };
